import { supabase } from './supabase';
import { CreditService } from './credits';
import { LimitEnforcementService } from './limit-enforcement-service';
import { broadcastCreditChange } from './credit-events';
import { AdCreativeCreditService } from './ad-creative-credits';

/**
 * Unified Credit Service - Single source of truth for all credit-related data
 * 
 * This service eliminates inconsistencies by providing a centralized interface
 * for credit calculations that all components should use.
 */

export interface UnifiedCreditData {
  // Analysis credits (simplified - no rollover)
  totalCredits: number;
  availableCredits: number;
  usedCredits: number;
  
  // Ad-creative credits (simplified - no rollover)
  adCreative: {
    totalCredits: number;
    availableCredits: number;
    usedCredits: number;
    purchasedCredits: number;
    monthlyAllowance: number;
    expiringCredits?: {
      amount: number;
      expiresIn: string;
    };
  };
  
  // Monthly subscription context
  monthlyAllowance: number;
  currentMonthUsed: number;
  
  // Plan information
  planName: string;
  planId: string;
  
  // Organization context
  organizationId: string | null;
  isOrganizationMember: boolean;
  
  // Additional metrics
  successRate: number;
  totalAnalyses: number;
  launchReadyCount: number;
  
  // Timestamps
  lastUpdated: string;
  nextRenewal?: string;
}

export interface CreditCalculationOptions {
  includeOrganization?: boolean;
  useCache?: boolean;
  cacheTimeoutMs?: number;
}

// Cache for credit data to prevent duplicate calculations
const creditCache = new Map<string, { data: UnifiedCreditData; timestamp: number }>();
const CACHE_DURATION = 30000; // 30 seconds

export class UnifiedCreditService {
  /**
   * Get unified credit data for a user - PRIMARY METHOD
   * This should be used by all components instead of individual services
   */
  static async getCreditData(
    userId: string, 
    options: CreditCalculationOptions = {}
  ): Promise<UnifiedCreditData> {
    try {
      const { includeOrganization = true, useCache = true, cacheTimeoutMs = CACHE_DURATION } = options;
      
      // Check cache first
      if (useCache) {
        const cached = creditCache.get(userId);
        if (cached && (Date.now() - cached.timestamp) < cacheTimeoutMs) {
          return cached.data;
        }
      }
      
      // Get user's organization context and determine target user for credits
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id, subscription_plan, team_role')
        .eq('id', userId)
        .single();
      
      if (userError && userError.code !== 'PGRST116') {
        console.error('❌ Error fetching user data:', userError);
        throw userError;
      }
      
      const organizationId = userData?.organization_id || null;
      const isOrganizationMember = !!organizationId;
      
      // Determine the target user for subscription_limits queries
      let targetUserId = userId;
      if (organizationId && userData?.team_role) {
        // Check if this user is the organization owner or a team member
        const { data: orgData } = await supabase
          .from('organizations')
          .select('created_by')
          .eq('id', organizationId)
          .single();
        
        if (orgData?.created_by && orgData.created_by !== userId) {
          // This is a team member (not the organization owner) - redirect to organization owner
          targetUserId = orgData.created_by;
          console.log('🔄 Team member detected - redirecting credit queries to organization owner:', {
            teamMember: userId,
            organizationOwner: targetUserId
          });
        }
      }
      
      // Get user IDs to include in calculations (user + organization members if applicable)
      let userIds = [userId];
      if (includeOrganization && organizationId) {
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', organizationId);
        
        if (orgMembers && orgMembers.length > 0) {
          userIds = orgMembers.map(m => m.id);
        }
      }
      
      // Get subscription-based credit information (team-aware)
      const { SubscriptionService } = await import('./subscription-service');
      const usageData = await SubscriptionService.getUserUsage(targetUserId);
      
      // Note: usageData.analysesLimit already includes purchased credits from SubscriptionService
      const totalCredits = usageData.analysesLimit;
      const currentUsed = usageData.analysesUsed;
      
      // Available credits = total credits (monthly + purchased) minus used (no rollover)
      const totalAvailableCredits = Math.max(0, totalCredits - currentUsed);
      
      // Get purchased credits separately for logging/debugging using team-aware RPC
      const { data: limitsData } = await supabase
        .rpc('get_team_aware_subscription_limits', { target_user_id: targetUserId })
        .single();
      
      const purchasedCredits = limitsData?.purchased_analysis_credits || 0;
      
      
      // Calculate analytics using organization-aware logic
      const analyticsData = await this.calculateAnalyticsForUsers(userIds);
      
      // Get ad-creative credit data (team-aware)
      const adCreativeBalance = await AdCreativeCreditService.getAdCreativeCreditBalance();
      const expiringCredits = await AdCreativeCreditService.getExpiringCredits();
      
      // Get plan name (for team members, show organization plan)
      let planName = 'Free Trial';
      let planId = 'trial';
      
      if (isOrganizationMember && organizationId) {
        // Get organization owner's plan (the user who created the organization)
        const { data: organization } = await supabase
          .from('organizations')
          .select('created_by')
          .eq('id', organizationId)
          .single();
        
        const { data: orgOwner, error: ownerError } = await supabase
          .from('users')
          .select('subscription_plan')
          .eq('id', organization?.created_by)
          .not('subscription_plan', 'is', null)
          .single();
        
        if (!ownerError && orgOwner?.subscription_plan) {
          const { data: plan } = await supabase
            .from('subscription_plans')
            .select('name, id')
            .eq('id', orgOwner.subscription_plan)
            .single();
          
          if (plan) {
            planName = `${plan.name} (Organization)`;
            planId = plan.id;
          }
        }
      } else {
        // Individual user - get their plan
        try {
          const { data: userPlan } = await supabase
            .from('subscription_plans')
            .select('name, id')
            .eq('id', userData?.subscription_plan || 'trial')
            .single();
          
          if (userPlan) {
            planName = userPlan.name;
            planId = userPlan.id;
          }
        } catch (error) {
          console.error('Error getting individual plan:', error);
        }
      }
      
      // Build unified data structure (simplified without rollover)
      const unifiedData: UnifiedCreditData = {
        totalCredits: totalCredits,
        availableCredits: totalAvailableCredits,
        usedCredits: currentUsed,
        adCreative: {
          totalCredits: (adCreativeBalance?.monthlyAllocation || 0) + 
                       (adCreativeBalance?.purchasedCredits || 0),
          availableCredits: adCreativeBalance?.totalAvailable || 0,
          usedCredits: adCreativeBalance?.usedThisPeriod || 0,
          purchasedCredits: adCreativeBalance?.purchasedCredits || 0,
          monthlyAllowance: adCreativeBalance?.monthlyAllocation || 0,
          expiringCredits: expiringCredits || undefined
        },
        monthlyAllowance: usageData.analysesLimit - (limitsData?.purchased_analysis_credits || 0),
        currentMonthUsed: currentUsed,
        planName: planName,
        planId: planId,
        organizationId,
        isOrganizationMember,
        successRate: analyticsData.successRate,
        totalAnalyses: analyticsData.totalAnalyses,
        launchReadyCount: analyticsData.launchReadyCount,
        lastUpdated: new Date().toISOString(),
        nextRenewal: undefined // TODO: Add renewal date from subscription
      };
      
      // Cache the result
      if (useCache) {
        creditCache.set(userId, {
          data: unifiedData,
          timestamp: Date.now()
        });
      }
      
      return unifiedData;
    } catch (error) {
      console.error('❌ Error in UnifiedCreditService.getCreditData:', error);
      throw error;
    }
  }
  
  /**
   * Calculate analytics for a set of users (organization-aware)
   */
  private static async calculateAnalyticsForUsers(userIds: string[]): Promise<{
    successRate: number;
    totalAnalyses: number;
    launchReadyCount: number;
  }> {
    try {
      // Get projects for these users
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('id')
        .in('user_id', userIds);
      
      if (projectsError) {
        console.error('❌ Error fetching projects:', projectsError);
        return { successRate: 0, totalAnalyses: 0, launchReadyCount: 0 };
      }
      
      const projectIds = projects?.map(p => p.id) || [];
      
      if (projectIds.length === 0) {
        return { successRate: 0, totalAnalyses: 0, launchReadyCount: 0 };
      }
      
      // Get creatives for these projects
      const { data: creatives, error: creativesError } = await supabase
        .from('creatives')
        .select('id')
        .in('project_id', projectIds);
      
      if (creativesError) {
        console.error('❌ Error fetching creatives:', creativesError);
        return { successRate: 0, totalAnalyses: 0, launchReadyCount: 0 };
      }
      
      const creativeIds = creatives?.map(c => c.id) || [];
      
      if (creativeIds.length === 0) {
        return { successRate: 0, totalAnalyses: 0, launchReadyCount: 0 };
      }
      
      // Get analyses for these creatives
      const { data: analyses, error: analysesError } = await supabase
        .from('analyses')
        .select('id, overall_score, created_at')
        .in('creative_id', creativeIds)
        .not('overall_score', 'is', null);
      
      if (analysesError) {
        console.error('❌ Error fetching analyses:', analysesError);
        return { successRate: 0, totalAnalyses: 0, launchReadyCount: 0 };
      }
      
      const totalAnalyses = analyses?.length || 0;
      const launchReadyCount = analyses?.filter(a => a.overall_score !== null && a.overall_score >= 80).length || 0;
      const successRate = totalAnalyses > 0 ? (launchReadyCount / totalAnalyses) * 100 : 0;
      
      return {
        successRate,
        totalAnalyses,
        launchReadyCount
      };
    } catch (error) {
      console.error('❌ Error calculating analytics:', error);
      return { successRate: 0, totalAnalyses: 0, launchReadyCount: 0 };
    }
  }
  
  /**
   * Get usage summary compatible with existing UsageMeterWidget (team-aware)
   */
  static async getUsageSummary(userId: string) {
    try {
      // Clear cache first to ensure fresh data for team members
      this.clearCache(userId);
      const creditData = await this.getCreditData(userId, { useCache: false });
      
      // Get user's organization context to determine if they're a team member
      const { data: userData } = await supabase
        .from('users')
        .select('organization_id, team_role')
        .eq('id', userId)
        .single();

      let organizationUserIds = [userId];
      if (userData?.organization_id && userData?.team_role) {
        // Check if this is an organization - get all member IDs for organization stats
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', userData.organization_id);
        
        if (orgMembers && orgMembers.length > 0) {
          organizationUserIds = orgMembers.map(m => m.id);
        }
      }
      
      // Get organization-aware project count
      const { data: projects } = await supabase
        .from('projects')
        .select('id')
        .in('user_id', organizationUserIds);
      
      const currentProjectsCount = projects?.length || 0;
      
      // Get organization team seat count (total org members)
      const currentTeamSeatsUsed = organizationUserIds.length;
      
      // Get basic usage summary as fallback
      const usageSummary = await LimitEnforcementService.getUsageSummary(userId);
      
      // Get team-aware limits directly to ensure correct limits are displayed
      const userLimits = await LimitEnforcementService.getUserLimits(userId);
      
      console.log('🔧 Debug - UnifiedCreditService.getUsageSummary:', {
        userId,
        userLimits: userLimits,
        usageSummary: {
          teamSeatsLimit: usageSummary.teamSeatsLimit,
          projectsLimit: usageSummary.projectsLimit,
          versionsLimit: usageSummary.versionsLimit,
          maxStorageBytes: usageSummary.maxStorageBytes
        }
      });
      
      // Override with unified data and organization-aware counts
      return {
        ...usageSummary,
        creditsRemaining: creditData.availableCredits,
        // Use total credits (plan + purchased) instead of just monthly allowance
        monthlyCredits: creditData.totalCredits,
        // Keep the team-aware currentAnalysesUsed from LimitEnforcementService instead of overriding
        currentAnalysesUsed: usageSummary.currentAnalysesUsed,
        rolloverCreditsAvailable: 0, // No rollover anymore
        totalCreditsAvailable: creditData.totalCredits,
        planName: creditData.planName,
        // Override with correct team-aware limits - force use userLimits if available
        teamSeatsLimit: userLimits?.teamSeatsLimit !== undefined ? userLimits.teamSeatsLimit : usageSummary.teamSeatsLimit,
        projectsLimit: userLimits?.projectsLimit !== undefined ? userLimits.projectsLimit : usageSummary.projectsLimit,
        versionsLimit: userLimits?.versionsLimit !== undefined ? userLimits.versionsLimit : usageSummary.versionsLimit,
        maxStorageBytes: userLimits?.maxStorageBytes !== undefined ? userLimits.maxStorageBytes : usageSummary.maxStorageBytes,
        // Recalculate remaining values with correct limits
        storageRemaining: Math.max(0, (userLimits?.maxStorageBytes || usageSummary.maxStorageBytes) - usageSummary.currentStorageUsed),
        storagePercentageUsed: (userLimits?.maxStorageBytes || usageSummary.maxStorageBytes) > 0 
          ? Math.round((usageSummary.currentStorageUsed / (userLimits?.maxStorageBytes || usageSummary.maxStorageBytes)) * 100)
          : 0,
        versionsRemaining: userLimits?.versionsLimit === -1 ? 999 : (userLimits?.versionsLimit || usageSummary.versionsLimit),
        // Organization-aware counts
        currentProjectsCount: currentProjectsCount,
        projectsRemaining: Math.max(0, (userLimits?.projectsLimit !== undefined ? userLimits.projectsLimit : usageSummary.projectsLimit || 1) - currentProjectsCount),
        currentTeamSeatsUsed: currentTeamSeatsUsed,
        teamSeatsRemaining: Math.max(0, (userLimits?.teamSeatsLimit !== undefined ? userLimits.teamSeatsLimit : usageSummary.teamSeatsLimit || 1) - currentTeamSeatsUsed),
      };
    } catch (error) {
      console.error('❌ Error in getUsageSummary:', error);
      // Fallback to original service
      return await LimitEnforcementService.getUsageSummary(userId);
    }
  }
  
  /**
   * Clear cache for a specific user or all users
   */
  static clearCache(userId?: string) {
    if (userId) {
      creditCache.delete(userId);
    } else {
      creditCache.clear();
    }
  }
  
  /**
   * Initialize - clear all caches on startup to ensure fresh data
   */
  static initialize() {
    this.clearCache();

    // Add debug methods to window for manual testing
    if (typeof window !== 'undefined') {
      (window as any).debugCredits = {
        clearCache: this.clearCache.bind(this),
        refreshUser: this.refreshCreditData.bind(this),
        getCacheStats: this.getCacheStats.bind(this)
      };
    }
  }
  
  /**
   * Force refresh credit data (bypasses cache)
   */
  static async refreshCreditData(userId: string): Promise<UnifiedCreditData> {
    console.log('🔄 Force refreshing credit data for user:', userId);
    // Clear any existing cache first
    this.clearCache(userId);
    return await this.getCreditData(userId, { useCache: false });
  }
  
  /**
   * Get simplified credit info for Dashboard display
   */
  static async getSimpleCreditInfo(userId: string, bypassCache: boolean = false): Promise<{
    credits: number;
    planName: string;
    usedThisMonth: number;
    monthlyAllowance: number;
  }> {
    try {
      const creditData = await this.getCreditData(userId, { useCache: !bypassCache });
      return {
        credits: creditData.availableCredits,
        planName: creditData.planName,
        usedThisMonth: creditData.currentMonthUsed,
        monthlyAllowance: creditData.monthlyAllowance
      };
    } catch (error) {
      console.error('❌ Error getting simple credit info:', error);
      return {
        credits: 0,
        planName: 'Free Trial',
        usedThisMonth: 0,
        monthlyAllowance: 3
      };
    }
  }
  
  /**
   * Update credit data after transactions
   */
  static async updateAfterTransaction(userId: string, transactionType: 'credit_used' | 'credit_added' | 'subscription_changed') {
    
    try {
      // Clear cache to force fresh calculation
      this.clearCache(userId);
      
      // Pre-calculate fresh data
      const freshData = await this.getCreditData(userId);
      
      console.log('✅ Credit data updated after transaction:', {
        userId,
        transactionType,
        newCredits: freshData.availableCredits,
        planName: freshData.planName
      });

      // Broadcast credit change event if needed
      if (transactionType === 'subscription_changed') {
        broadcastCreditChange(
          'subscription_changed',
          userId,
          0, // No direct credit change for subscription changes
          freshData.availableCredits,
          {
            reason: `Subscription changed to ${freshData.planName}`
          }
        );
      }
    } catch (error) {
      console.error('❌ Error updating credit data after transaction:', error);
      // Don't throw - this shouldn't break the user's flow
    }
  }
  
  /**
   * Get cache statistics for debugging
   */
  static getCacheStats() {
    return {
      size: creditCache.size,
      entries: Array.from(creditCache.keys()),
      oldestEntry: creditCache.size > 0 ? Math.min(...Array.from(creditCache.values()).map(v => v.timestamp)) : null
    };
  }
  
  /**
   * Monitor data consistency across different sources
   */
  static async checkDataConsistency(userId: string): Promise<{
    isConsistent: boolean;
    unifiedCredits: number;
    authContextCredits: number;
    limitServiceCredits: number;
    differences: string[];
  }> {
    try {
      const [unifiedData, limitSummary] = await Promise.all([
        this.getCreditData(userId),
        LimitEnforcementService.getUsageSummary(userId)
      ]);
      
      const differences: string[] = [];
      
      // Compare available credits
      if (unifiedData.availableCredits !== limitSummary.creditsRemaining) {
        differences.push(`Available credits: Unified(${unifiedData.availableCredits}) vs LimitService(${limitSummary.creditsRemaining})`);
      }
      
      // Compare monthly allowance
      if (unifiedData.monthlyAllowance !== limitSummary.monthlyCredits) {
        differences.push(`Monthly allowance: Unified(${unifiedData.monthlyAllowance}) vs LimitService(${limitSummary.monthlyCredits})`);
      }
      
      // Compare plan name
      if (unifiedData.planName !== limitSummary.planName) {
        differences.push(`Plan name: Unified(${unifiedData.planName}) vs LimitService(${limitSummary.planName})`);
      }
      
      return {
        isConsistent: differences.length === 0,
        unifiedCredits: unifiedData.availableCredits,
        authContextCredits: 0, // TODO: Add auth context comparison
        limitServiceCredits: limitSummary.creditsRemaining,
        differences
      };
    } catch (error) {
      console.error('❌ Error checking data consistency:', error);
      return {
        isConsistent: false,
        unifiedCredits: 0,
        authContextCredits: 0,
        limitServiceCredits: 0,
        differences: ['Error during consistency check']
      };
    }
  }
}

export default UnifiedCreditService;